import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../../api';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { ArrowLeft, Briefcase, Printer, FileSpreadsheet } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../AuthContext';
import { openPrintWindow, buildPrintHTML } from '../../utils/printUtils';
import { exportToExcel } from '../../utils/excelExport';

const GroundedEnterprisesReport = () => {
  const { isAdmin } = useAuth();
  const [branches, setBranches] = useState([]);
  const [data, setData] = useState(null);
  const [branch, setBranch] = useState('all');
  const [loanType, setLoanType] = useState('all');
  const [loanStatus, setLoanStatus] = useState('all');
  const [loading, setLoading] = useState(false);

  useEffect(() => { api.get('/branches').then(r => setBranches(r.data)).catch(() => {}); }, []);

  const fetchReport = async () => {
    setLoading(true);
    try {
      const params = { loan_status: loanStatus };
      if (branch !== 'all') params.branch_id = branch;
      if (loanType !== 'all') params.loan_type = loanType;
      const resp = await api.get('/reports/grounded-enterprises', { params });
      setData(resp.data);
    } catch { toast.error('Failed to fetch report'); }
    finally { setLoading(false); }
  };

  const getBranchName = () => branch === 'all' ? 'All Branches' : branches.find(b => b.id === branch)?.name || 'Unknown';

  const handleExcel = () => {
    if (!data || !(data.enterprises||[]).length) { toast.error('Generate the report first'); return; }
    const rows = (data.enterprises||[]).map((e, i) => ({
      'SN': i + 1,
      'Entrepreneur': e.entrepreneur_name || '-',
      'Branch': e.branch_name || '-',
      'Enterprise': e.enterprise_name || '-',
      'Operation Type': e.operation_type || '-',
      'CEF Loans #': e.cef_loan_count || 0,
      'CEF Amount': Math.round(e.cef_amount || 0),
      'CIF Loans #': e.cif_loan_count || 0,
      'CIF Amount': Math.round(e.cif_amount || 0),
      'Bank Loans #': e.bank_loan_count || 0,
      'Bank Amount': Math.round(e.bank_amount || 0),
      'Total Loan Amount': Math.round(e.total_loan_amount || 0),
    }));
    exportToExcel({
      filename: `WWAGS_Grounded_Enterprises_${getBranchName().replace(/\s+/g,'_')}`,
      sheetName: 'Grounded Enterprises',
      rows,
      kpis: [
        { label: 'Branch', value: getBranchName() },
        { label: 'Total Grounded', value: data.total_grounded || 0 },
        { label: 'CEF Loans', value: `${data.total_cef_loans || 0} (Rs.${(data.total_cef_amount||0).toLocaleString('en-IN')})` },
        { label: 'CIF Loans', value: `${data.total_cif_loans || 0} (Rs.${(data.total_cif_amount||0).toLocaleString('en-IN')})` },
        { label: 'Bank Loans', value: `${data.total_bank_loans || 0} (Rs.${(data.total_bank_amount||0).toLocaleString('en-IN')})` },
      ],
    });
  };

  const handlePrint = () => {
    if (!data) return;
    const rows = (data.enterprises||[]).map((e, i) => `<tr style="background:${i%2===0?'#fff':'#f9f9f9'}"><td>${e.entrepreneur_name}</td><td>${e.branch_name}</td><td>${e.enterprise_name}</td><td>${e.operation_type}</td><td style="text-align:center">${e.cef_loan_count}</td><td style="text-align:right">Rs.${(e.cef_amount||0).toLocaleString('en-IN')}</td><td style="text-align:center">${e.cif_loan_count}</td><td style="text-align:right">Rs.${(e.cif_amount||0).toLocaleString('en-IN')}</td><td style="text-align:center">${e.bank_loan_count}</td><td style="text-align:right">Rs.${(e.bank_amount||0).toLocaleString('en-IN')}</td><td style="text-align:right;font-weight:bold">Rs.${(e.total_loan_amount||0).toLocaleString('en-IN')}</td></tr>`).join('');
    const body = `<div class="cards"><div class="card"><small>Total Grounded</small><b>${data.total_grounded||0}</b></div><div class="card"><small>CEF Loans</small><b>${data.total_cef_loans||0} (Rs.${(data.total_cef_amount||0).toLocaleString('en-IN')})</b></div><div class="card"><small>CIF Loans</small><b>${data.total_cif_loans||0} (Rs.${(data.total_cif_amount||0).toLocaleString('en-IN')})</b></div><div class="card"><small>Bank Loans</small><b>${data.total_bank_loans||0} (Rs.${(data.total_bank_amount||0).toLocaleString('en-IN')})</b></div></div><table><thead><tr><th>Entrepreneur</th><th>Branch</th><th>Enterprise</th><th>Type</th><th>CEF#</th><th>CEF Amt</th><th>CIF#</th><th>CIF Amt</th><th>Bank#</th><th>Bank Amt</th><th>Total</th></tr></thead><tbody>${rows}</tbody></table>`;
    openPrintWindow(buildPrintHTML('Grounded Enterprises Report', body, getBranchName()));
  };

  return (
    <div className="p-4 md:p-6 lg:p-8" data-testid="grounded-enterprises-report-page">
      <div className="flex items-center gap-3 mb-6">
        <Link to="/reports" className="text-slate-500 hover:text-slate-700"><ArrowLeft className="w-5 h-5" /></Link>
        <div><h1 className="text-2xl font-bold text-slate-900">Grounded Enterprises</h1><p className="text-sm text-slate-600">Enterprises with CEF, CIF, or Bank loans</p></div>
      </div>

      <div className="flex flex-wrap items-end gap-3 mb-6">
        {isAdmin() && <Select value={branch} onValueChange={setBranch}><SelectTrigger className="w-48"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">All Branches</SelectItem>{branches.map(b => <SelectItem key={b.id} value={b.id}>{b.code} - {b.name}</SelectItem>)}</SelectContent></Select>}
        <Select value={loanType} onValueChange={setLoanType}><SelectTrigger className="w-40"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">All Loan Types</SelectItem><SelectItem value="CEF">CEF</SelectItem><SelectItem value="CIF">CIF</SelectItem><SelectItem value="Bank Loan">Bank Loan</SelectItem></SelectContent></Select>
        <Select value={loanStatus} onValueChange={setLoanStatus}>
          <SelectTrigger className="w-32" data-testid="grounded-status"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="closed">Closed</SelectItem>
          </SelectContent>
        </Select>
        <Button onClick={fetchReport} className="bg-orange-700 hover:bg-orange-800 text-white" data-testid="generate-grounded">Generate</Button>
        {data && <Button variant="outline" onClick={handlePrint}><Printer className="w-4 h-4 mr-1" />Print</Button>}
        {data && <Button variant="outline" onClick={handleExcel} className="border-emerald-300 text-emerald-700 hover:bg-emerald-50" data-testid="excel-btn"><FileSpreadsheet className="w-4 h-4 mr-1" />Excel</Button>}
      </div>

      {loading && <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-700"></div></div>}

      {data && !loading && (
        <div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 text-center"><p className="text-xs text-orange-600 mb-1">Total Grounded</p><p className="text-xl font-bold text-orange-800">{data.total_grounded}</p></div>
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-center"><p className="text-xs text-emerald-600 mb-1">CEF Loans</p><p className="text-lg font-bold text-emerald-800">{data.total_cef_loans} | Rs.{(data.total_cef_amount||0).toLocaleString('en-IN')}</p></div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-center"><p className="text-xs text-blue-600 mb-1">CIF Loans</p><p className="text-lg font-bold text-blue-800">{data.total_cif_loans} | Rs.{(data.total_cif_amount||0).toLocaleString('en-IN')}</p></div>
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 text-center"><p className="text-xs text-purple-600 mb-1">Bank Loans</p><p className="text-lg font-bold text-purple-800">{data.total_bank_loans} | Rs.{(data.total_bank_amount||0).toLocaleString('en-IN')}</p></div>
          </div>

          <div className="overflow-x-auto bg-white rounded-lg border border-slate-200">
            <table className="w-full text-xs">
              <thead className="bg-orange-700 text-white"><tr><th className="px-3 py-2 text-left">Entrepreneur</th><th className="px-3 py-2 text-left">Branch</th><th className="px-3 py-2 text-left">Enterprise</th><th className="px-3 py-2 text-left">Type</th><th className="px-3 py-2 text-center">CEF#</th><th className="px-3 py-2 text-right">CEF Amt</th><th className="px-3 py-2 text-center">CIF#</th><th className="px-3 py-2 text-right">CIF Amt</th><th className="px-3 py-2 text-center">Bank#</th><th className="px-3 py-2 text-right">Bank Amt</th><th className="px-3 py-2 text-right">Total</th></tr></thead>
              <tbody className="divide-y divide-slate-100">
                {(data.enterprises||[]).map((e, i) => (
                  <tr key={e.entrepreneur_id} className={`${i % 2 === 0 ? '' : 'bg-slate-50'} hover:bg-slate-100`}>
                    <td className="px-3 py-2"><Link to={`/entrepreneurs/${e.entrepreneur_id}`} className="text-blue-700 hover:underline font-medium">{e.entrepreneur_name}</Link></td>
                    <td className="px-3 py-2">{e.branch_name}</td>
                    <td className="px-3 py-2">{e.enterprise_name}</td>
                    <td className="px-3 py-2">{e.operation_type}</td>
                    <td className="px-3 py-2 text-center">{e.cef_loan_count}</td>
                    <td className="px-3 py-2 text-right">Rs.{(e.cef_amount||0).toLocaleString('en-IN')}</td>
                    <td className="px-3 py-2 text-center">{e.cif_loan_count}</td>
                    <td className="px-3 py-2 text-right">Rs.{(e.cif_amount||0).toLocaleString('en-IN')}</td>
                    <td className="px-3 py-2 text-center">{e.bank_loan_count}</td>
                    <td className="px-3 py-2 text-right">Rs.{(e.bank_amount||0).toLocaleString('en-IN')}</td>
                    <td className="px-3 py-2 text-right font-bold">Rs.{(e.total_loan_amount||0).toLocaleString('en-IN')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {!data && !loading && <div className="text-center py-12 text-slate-500"><Briefcase className="w-12 h-12 mx-auto mb-3 text-slate-300" /><p>Click "Generate" to view grounded enterprises</p></div>}
    </div>
  );
};

export default GroundedEnterprisesReport;
